import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, User, Heart, Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api, { mediaUrl, kr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { HalalBadge } from "@/components/HalalBadge";
import { E } from "@/components/Editable";
import { useDevice, dv } from "@/components/Device";
import { CategoryIcon } from "@/components/CategoryIcon";

export function Header() {
  const navigate = useNavigate();
  const { user, logout, cartCount, setCartOpen, favorites, setSurpriseOpen } = useApp();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { const h = () => setMenuOpen(true); window.addEventListener("b1m:openmenu", h); return () => window.removeEventListener("b1m:openmenu", h); }, []);
  const [cats, setCats] = useState([]);
  const [allCats, setAllCats] = useState([]);
  const childrenOf = (id) => allCats.filter((c) => c.parent_id === id);
  const boxRef = useRef(null);
  const device = useDevice();
  const isPc = device === "desktop";

  useEffect(() => {
    api.get("/categories?visible_only=true").then(({ data }) => { setCats(data.filter((c) => !c.parent_id)); setAllCats(data); }).catch(() => {});
  }, []);

  useEffect(() => {
    const handler = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setResults(data.products); setOpen(true); } catch (e) { console.error("Søgning fejlede:", e); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/produkter?search=${encodeURIComponent(q)}`); setOpen(false); }
  };

  const iconBtn = "relative rounded-full p-2.5 text-foreground/80 [transition:transform_.2s_ease,color_.2s_ease,filter_.2s_ease] hover:scale-105 hover:text-primary hover:[filter:drop-shadow(0_0_10px_rgba(236,30,121,0.75))]";

  return (
    <header className="relative z-50 border-b border-border bg-background/95 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-4">
        {/* Top row: menu · logo · halal · icons */}
        <div className={`flex items-center justify-between gap-3 ${isPc ? "pt-3" : "pt-2"}`}>
          <div className={`flex min-w-0 items-center ${isPc ? "gap-4" : "gap-2"}`}>
            <E id="menu" label="Hamburger-menu" defaultVisibility={{ desktop: false, tablet: true, mobile: true }}>
              <button onClick={() => setMenuOpen(true)} data-testid="mobile-menu-btn" aria-label="Menu">
                <Menu className="h-6 w-6" />
              </button>
            </E>
            <E id="logo" label="Logo" kind="image" className="shrink-0">
              {(el) => (
                <Link to="/" data-testid="header-logo" className="flex shrink-0 items-center">
                  <img src={el.src ? mediaUrl(el.src) : "/brand/logo.png"} alt="BARE 1 MERE" className={`w-auto object-contain ${dv(device, { desktop: "h-16", tablet: "h-14", mobile: "h-12" })}`} />
                </Link>
              )}
            </E>
            <div className="shrink-0">
              <E id="halal" label="HALAL-mærke" kind="text" defaultVisibility={{ desktop: true, tablet: true, mobile: false }}>{(el) => <HalalBadge size={isPc ? "md" : "sm"} text={el.text} />}</E>
            </div>
          </div>

          <E id="icons" label="Ikon-menu" display="flex" className="items-center gap-1 sm:gap-1.5">
            <E id="fav" label="Favorit-ikon">
            <Link to="/favoritter" data-testid="header-favorites" className={iconBtn} aria-label="Favoritter">
              <Heart className="h-5 w-5" />
              {favorites.length > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">{favorites.length}</span>}
            </Link>
            </E>
            <E id="account" label="Bruger-ikon">

            {user ? (
              <div className="group relative">
                <button data-testid="header-account" className={iconBtn} aria-label="Konto"><User className="h-5 w-5" /></button>
                <div className="invisible absolute right-0 top-12 z-[70] w-52 rounded-2xl border border-border bg-popover p-2 opacity-0 shadow-2xl transition-all group-hover:visible group-hover:opacity-100">
                  <p className="px-3 py-2 text-xs text-muted-foreground">Hej, {user.name?.split(" ")[0]}</p>
                  <Link to="/konto" className="block rounded-lg px-3 py-2 text-sm hover:bg-secondary" data-testid="menu-account">Min konto</Link>
                  {user.role === "admin" && (
                    <Link to="/admin" className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-primary hover:bg-secondary" data-testid="menu-admin">
                      <LayoutDashboard className="h-4 w-4" /> Admin panel
                    </Link>
                  )}
                  <button onClick={logout} data-testid="menu-logout" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-destructive hover:bg-secondary">
                    <LogOut className="h-4 w-4" /> Log ud
                  </button>
                </div>
              </div>
            ) : (
              <Link to="/login" data-testid="header-login" className={iconBtn} aria-label="Log ind"><User className="h-5 w-5" /></Link>
            )}
            </E>
            <E id="cart" label="Kurv-ikon">
            <button data-testid="header-cart" onClick={() => setCartOpen(true)} className={iconBtn} aria-label="Kurv">
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">{cartCount}</span>}
            </button>
            </E>
          </E>
        </div>

        {/* Search row (full width) */}
        <div ref={boxRef} className={`relative ${isPc ? "py-3" : "py-2"}`}>
          <E id="search" label="Søgebar" kind="text" display="block">{(el) => (
          <form onSubmit={submitSearch}>
            <div className="flex items-center rounded-full border border-primary/25 bg-[#141017] px-5 shadow-[0_0_0_1px_rgba(236,30,121,0.12),0_8px_24px_-6px_rgba(236,30,121,0.35)] transition-all focus-within:border-primary/70 focus-within:shadow-[0_0_0_2px_rgba(236,30,121,0.4),0_10px_28px_-4px_rgba(236,30,121,0.5)]">
              <Search className="h-5 w-5 text-primary" />
              <input
                data-testid="search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onFocus={() => results.length && setOpen(true)}
                placeholder={el.text || "Søg efter slik, chokolade, sodavand…"}
                className={`w-full bg-transparent px-3 text-white outline-none placeholder:text-white/45 ${isPc ? "py-3 text-base" : "py-2.5 text-sm"}`}
              />
            </div>
          </form>
          )}</E>
          <AnimatePresence>
            {open && results.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                data-testid="search-results"
                className="absolute left-0 right-0 top-full z-50 mt-1 max-h-96 overflow-auto rounded-2xl border border-border bg-popover p-2 shadow-2xl"
              >
                {results.slice(0, 8).map((p) => (
                  <button
                    key={p.id}
                    onClick={() => { navigate(`/produkter/${p.slug}`); setOpen(false); setQ(""); }}
                    className="flex w-full items-center gap-3 rounded-xl p-2 text-left transition-colors hover:bg-secondary"
                  >
                    <img src={mediaUrl(p.main_image)} alt="" className="h-10 w-10 rounded-lg object-cover" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.producer}</p>
                    </div>
                    <span className="text-sm font-bold text-primary">{kr(p.effective_price)}</span>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Desktop category nav */}
      <E id="nav" label="Produktmenu (fuld)" kind="text" display="block" defaultVisibility={{ desktop: true, tablet: false, mobile: false }}>
      <nav className="border-t border-border/60">
        <div className="mx-auto flex max-w-7xl items-center gap-6 px-4 py-2 text-sm">
          <Link to="/produkter" className="font-medium text-muted-foreground transition-colors hover:text-primary">Alle produkter</Link>
          {cats.map((c) => { const subs = childrenOf(c.id); return (
            <div key={c.id} className="group relative">
              <Link to={`/kategori/${c.slug}`} data-testid={`nav-cat-${c.slug}`} className="flex items-center font-medium text-muted-foreground transition-colors hover:text-primary">
                <CategoryIcon cat={c} size={18} className="mr-1 align-middle" />{c.name}
              </Link>
              {subs.length > 0 && (
                <div className="invisible absolute left-0 top-full z-[65] min-w-[190px] -translate-y-1 rounded-2xl border border-border bg-popover p-2 opacity-0 shadow-2xl transition-all duration-150 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100" data-testid={`nav-subcats-${c.slug}`}>
                  {subs.map((ch) => (
                    <Link key={ch.id} to={`/kategori/${ch.slug}`} data-testid={`nav-subcat-${ch.slug}`} className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-primary">
                      <CategoryIcon cat={ch} size={14} className="align-middle" />{ch.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ); })}
          <button onClick={() => setSurpriseOpen(true)} data-testid="surprise-nav-btn" className="ml-auto font-bold text-primary">🎁 Overrask mig</button>
          <Link to="/tilbud" className="font-bold text-primary">🔥 Tilbud</Link>
        </div>
      </nav>
      </E>

      {/* Mobile drawer */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setMenuOpen(false)} className="fixed inset-0 z-50 bg-black/60" />
            <motion.div initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ type: "tween" }} className="fixed left-0 top-0 z-50 h-full w-72 border-r border-border bg-background p-5">
              <div className="mb-4 flex items-center justify-between">
                <HalalBadge size="sm" />
                <button onClick={() => setMenuOpen(false)}><X className="h-6 w-6" /></button>
              </div>
              <Link to="/produkter" onClick={() => setMenuOpen(false)} className="block rounded-lg px-3 py-3 text-sm font-medium hover:bg-secondary">Alle produkter</Link>
              {cats.map((c) => { const subs = childrenOf(c.id); return (
                <div key={c.id}>
                  <Link to={`/kategori/${c.slug}`} onClick={() => setMenuOpen(false)} className="block rounded-lg px-3 py-3 text-sm font-medium hover:bg-secondary">
                    <CategoryIcon cat={c} size={20} className="mr-2 align-middle" />{c.name}
                  </Link>
                  {subs.map((ch) => (
                    <Link key={ch.id} to={`/kategori/${ch.slug}`} onClick={() => setMenuOpen(false)} className="block rounded-lg px-3 py-2 pl-10 text-xs text-muted-foreground hover:bg-secondary hover:text-primary">
                      <CategoryIcon cat={ch} size={16} className="mr-2 align-middle" />{ch.name}
                    </Link>
                  ))}
                </div>
              ); })}
              <Link to="/tilbud" onClick={() => setMenuOpen(false)} className="block rounded-lg px-3 py-3 text-sm font-bold text-primary">🔥 Aktuelle tilbud</Link>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
